import { ErrorType } from '../domains/common.domain';
import { TaxIdBase } from './tax-id-base';

export class BrazilTaxId extends TaxIdBase {
  private readonly CPF_FIRST_WEIGHTS: number[] = [10, 9, 8, 7, 6, 5, 4, 3, 2];
  private readonly CPF_SECOND_WEIGHTS: number[] = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
  private readonly CNPJ_FIRST_WEIGHTS: number[] = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  private readonly CNPJ_SECOND_WEIGHTS: number[] = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  private readonly COEFFICIENT: number = 11;
  private readonly ASCII_ZERO: number = 48;
  private readonly STATE_TAX_ID_REGEX: RegExp = /^[\d\/\-.]+$/;
  private readonly STATE_TAX_ID_REPLACE_REGEX: RegExp = /[^\d\/\-.]/g;

  public override getFormattedStateTaxId(value: string): string {
    if (!value) return '';
    return value.replaceAll(this.STATE_TAX_ID_REPLACE_REGEX, '');
  }

  public override getFormattedTaxId(value: string, isOnlyCPF: boolean = false): string {
    value = value.trim();
    if (!value) return '';
    const taxId: string = isOnlyCPF ? this.getDigits(value) : this.getAlphanumericTaxId(value);
    const isCNPJ: boolean = !isOnlyCPF && (value.indexOf('.') === 2 || taxId.length > 11);
    let formatted: string = isCNPJ ? this.formatCnpj(taxId) : this.formatCpf(taxId);

    const lastChar: string | undefined = value.charAt(value.length - 1);
    if (lastChar && './-'.includes(lastChar)) {
      formatted += lastChar;
    }

    return formatted;
  }

  private formatCnpj(taxId: string): string {
    if (taxId.length < 6) {
      return taxId.replace(/([A-Z\d]{2})([A-Z\d]{1,3})/, '$1.$2');
    }
    if (taxId.length < 9) {
      return taxId.replace(/([A-Z\d]{2})([A-Z\d]{3})([A-Z\d]{1,3})/, '$1.$2.$3');
    }
    if (taxId.length < 13) {
      return taxId.replace(/([A-Z\d]{2})([A-Z\d]{3})([A-Z\d]{3})([A-Z\d]{0,4})/, '$1.$2.$3/$4');
    }
    return taxId.replace(/([A-Z\d]{2})([A-Z\d]{3})([A-Z\d]{3})([A-Z\d]{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
  }

  private formatCpf(taxId: string): string {
    if (taxId.length < 7) {
      return taxId.replace(/(\d{3})(\d{1,3})/, '$1.$2');
    }
    if (taxId.length < 10) {
      return taxId.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
    }
    return taxId.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  }

  public override getFormattedNationalId(value: string): string {
    return this.getFormattedTaxId(value, true);
  }

  public override isNationalIdValid(nationalId: string): boolean {
    return this.isTaxIdValid(nationalId, true);
  }

  public override isTaxIdValid(taxId: string, isOnlyCPF: boolean = false): boolean {
    taxId = isOnlyCPF ? this.getDigits(taxId.trim()) : this.getAlphanumericTaxId(taxId.trim());

    const hasValidLength: boolean = isOnlyCPF ? taxId.length === 11 : taxId.length === 11 || taxId.length === 14;
    if (!hasValidLength) {
      return false;
    }

    const firstDigits: string = taxId.substring(0, taxId.length - 2);
    const lastDigits: string = taxId.substring(taxId.length - 2);

    if (taxId.length === 11 && !/^\d+$/.test(firstDigits)) {
      return false;
    }

    return this.getLastDigits(firstDigits) === lastDigits;
  }

  public override validateStateTaxId(taxId: string): ErrorType | undefined {
    const value: string = taxId.trim();
    if (!value) return undefined;

    const numberOfDigits: number = value.replace(/[^0-9]/g, '').length;
    const isValid = this.STATE_TAX_ID_REGEX.test(value) && numberOfDigits > 0 && numberOfDigits < 15;
    return isValid ? undefined : 'invalid';
  }

  private getDigits(value: string): string {
    return value.replace(/\D/g, '');
  }

  private getAlphanumericTaxId(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  }

  private getLastDigits(value: string): string {
    if (!value) return '';

    const isCPF: boolean = value.length === 9;
    const firstWeights: number[] = isCPF ? this.CPF_FIRST_WEIGHTS : this.CNPJ_FIRST_WEIGHTS;
    const secondWeights: number[] = isCPF ? this.CPF_SECOND_WEIGHTS : this.CNPJ_SECOND_WEIGHTS;

    const firstDigit: number = this.calcLastDigit(value, firstWeights);
    const secondDigit: number = this.calcLastDigit(value, secondWeights, firstDigit);

    return `${firstDigit}${secondDigit}`;
  }

  private calcLastDigit(value: string, weights: number[], additionalDigit: number | null = null): number {
    const digits: number[] = value.split('').map(char => (char.codePointAt(0) ?? 0) - this.ASCII_ZERO);
    let sum: number = digits.reduce((acc, digit, index) => acc + digit * weights[index], 0);

    if (additionalDigit !== null) {
      sum += additionalDigit * weights[weights.length - 1];
    }

    const modResult: number = sum % this.COEFFICIENT;
    return modResult < 2 ? 0 : this.COEFFICIENT - modResult;
  }
}

export const BrazilTaxIdHelper = new BrazilTaxId();
